import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Search, RotateCcw, Plus, Trash2, FileText, Settings, X, Edit, ArrowLeft } from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError, showSuccess } from "../../../utils/toast";

interface LogMonitorSettingsProps {
    asset_id: string;
    client_id: string;
    agent_id?: string;
    onBack?: () => void;
}

const DEFAULT_SOURCES = [
    "linux_syslog",
    "linux_auth",
    "linux_kern",
    "windows_event_log",
    "windows_event_system",
    "windows_event_security",
    "windows_event_application",
    "macos_log"
];

const LogMonitorSettings: React.FC<LogMonitorSettingsProps> = ({
    asset_id,
    client_id,
    agent_id,
    onBack
}) => {
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [configData, setConfigData] = useState<any>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<"add" | "edit">("add");
    const [editingSourceId, setEditingSourceId] = useState<string | null>(null);
    const [servicesList, setServicesList] = useState<any[]>([]);
    const [showServicesDropdown, setShowServicesDropdown] = useState(false);

    // Add/Edit form state
    const [sourceLabel, setSourceLabel] = useState("");
    const [sourceType, setSourceType] = useState("");
    const [sourcePaths, setSourcePaths] = useState("");
    const [sourceMaxLines, setSourceMaxLines] = useState<number>(500);
    const [sourcePollInterval, setSourcePollInterval] = useState<number>(300);
    const [sourceFilterKeywords, setSourceFilterKeywords] = useState("");
    const [sourceExcludeKeywords, setSourceExcludeKeywords] = useState("");

    const fetchConfig = useCallback(async () => {
        setLoading(true);
        try {
            const params = { asset_id, client_id };
            const response = await baseapi.get("/asset_siem_log_config", { params });

            if (response.data.status && response.data.data && response.data.data.length > 0) {
                const item = response.data.data[0];
                let parsedConfig = item.config;
                if (typeof parsedConfig === 'string') {
                    try {
                        parsedConfig = JSON.parse(parsedConfig);
                    } catch (e) {
                        console.error("Failed to parse config", e);
                    }
                }
                setConfigData({
                    ...item,
                    config: parsedConfig
                });
            } else {
                setConfigData(null);
            }
        } catch (err: any) {
            if (err.response?.status !== 404) {
                showError(err.message || "Failed to load log monitor config");
            }
            setConfigData(null);
        }
        setLoading(false);
    }, [asset_id, client_id]);

    const fetchServices = useCallback(async () => {
        try {
            const params = { asset_id, client_id };
            const response = await baseapi.get("/asset_services", { params });
            if (response.data && response.data.data) {
                setServicesList(response.data.data);
            }
        } catch (err) {
            console.error("Failed to fetch services", err);
        }
    }, [asset_id, client_id]);

    useEffect(() => {
        fetchConfig();
        fetchServices();
    }, [fetchConfig, fetchServices]);

    const saveConfig = async (newConfig: any) => {
        try {
            const payload = {
                asset_id: parseInt(asset_id, 10),
                agent_id: agent_id ? parseInt(agent_id, 10) : configData?.agent_id,
                client_id: parseInt(client_id, 10),
                config: newConfig
            };
            let response;
            if (configData && configData.id) {
                // If record exists, do PUT with asset_id in URL path
                response = await baseapi.put(`/asset_siem_log_config/${asset_id}/`, payload);
            } else {
                // If no record exists, do POST
                response = await baseapi.post("/asset_siem_log_config/", payload);
            }
            if (response.data) {
                showSuccess("Configuration updated successfully");
                fetchConfig();
            }
        } catch (error: any) {
            showError(error.message || "Failed to update configuration");
        }
    };

    const handleToggleSource = (sourceId: string) => {
        if (!configData || !configData.config) return;
        const updatedSources = configData.config.sources.map((src: any) => {
            if (src.id === sourceId) {
                return { ...src, enabled: !src.enabled };
            }
            return src;
        });
        const hasAnyEnabled = updatedSources.some((src: any) => src.enabled);
        const updatedConfig = {
            ...configData.config,
            enabled: hasAnyEnabled ? true : configData.config.enabled,
            sources: updatedSources
        };
        setConfigData({ ...configData, config: updatedConfig });
        saveConfig(updatedConfig);
    };

    const handleDeleteSource = (sourceId: string) => {
        if (!configData || !configData.config) return;
        const updatedSources = configData.config.sources.filter((src: any) => src.id !== sourceId);
        const updatedConfig = { ...configData.config, sources: updatedSources };
        setConfigData({ ...configData, config: updatedConfig });
        saveConfig(updatedConfig);
    };

    const openAddModal = () => {
        setModalMode("add");
        setEditingSourceId(null);
        setSourceLabel("");
        setSourceType("file_tail");
        setSourcePaths("");
        setSourceMaxLines(500);
        setSourcePollInterval(300);
        setSourceFilterKeywords("");
        setSourceExcludeKeywords("");
        setIsModalOpen(true);
    };

    const openEditModal = (source: any) => {
        setModalMode("edit");
        setEditingSourceId(source.id);
        setSourceLabel(source.label || source.id);
        setSourceType(source.type || "file_tail");

        if (source.params) {
            setSourcePaths(source.params.paths ? source.params.paths.join(", ") : "");
            setSourceMaxLines(source.params.max_lines || 500);
            setSourcePollInterval(source.params.poll_interval_seconds || 300);
            setSourceFilterKeywords(source.params.filter_keywords ? source.params.filter_keywords.join(", ") : "");
            setSourceExcludeKeywords(source.params.exclude_keywords ? source.params.exclude_keywords.join(", ") : "");
        } else {
            setSourcePaths("");
            setSourceMaxLines(500);
            setSourcePollInterval(300);
            setSourceFilterKeywords("");
            setSourceExcludeKeywords("");
        }
        setIsModalOpen(true);
    };

    const handleSaveSource = (e: React.FormEvent) => {
        e.preventDefault();
        if (!sourceLabel.trim()) {
            showError("Label is required");
            return;
        }

        const generatedId = modalMode === "add" ? sourceLabel.trim().toLowerCase().replace(/\s+/g, '_') : (editingSourceId || "");

        const newSource: any = {
            id: generatedId,
            enabled: true,
            label: sourceLabel.trim()
        };

        newSource.type = sourceType || "file_tail";

        if (newSource.type === "file_tail" || sourcePaths.trim()) {
            newSource.params = {
                paths: sourcePaths.split(',').map(s => s.trim()).filter(Boolean),
                max_lines: sourceMaxLines,
                poll_interval_seconds: sourcePollInterval,
                filter_keywords: sourceFilterKeywords.split(',').map(s => s.trim()).filter(Boolean),
                exclude_keywords: sourceExcludeKeywords.split(',').map(s => s.trim()).filter(Boolean),
            };
        }

        let updatedConfig;
        if (configData && configData.config) {
            if (modalMode === "add") {
                const exists = configData.config.sources.find((s: any) => s.id === newSource.id);
                if (exists) {
                    showError("Source ID already exists based on this label");
                    return;
                }
                const updatedSources = [...configData.config.sources, newSource];
                const hasAnyEnabled = updatedSources.some((src: any) => src.enabled);
                updatedConfig = {
                    ...configData.config,
                    enabled: hasAnyEnabled ? true : configData.config.enabled,
                    sources: updatedSources
                };
            } else {
                // Edit
                const updatedSources = configData.config.sources.map((s: any) => {
                    if (s.id === editingSourceId) {
                        return { ...s, ...newSource, enabled: s.enabled }; // preserve existing enabled state
                    }
                    return s;
                });
                const hasAnyEnabled = updatedSources.some((src: any) => src.enabled);
                updatedConfig = {
                    ...configData.config,
                    enabled: hasAnyEnabled ? true : configData.config.enabled,
                    sources: updatedSources
                };
            }
        } else {
            updatedConfig = {
                enabled: true,
                batch_size: 100,
                queue_name: "agent:siem_logs_queue",
                sources: [newSource]
            };
        }

        setConfigData({ ...configData, config: updatedConfig });
        saveConfig(updatedConfig);
        setIsModalOpen(false);
    };

    const handleToggleGlobal = () => {
        if (!configData || !configData.config) {
            const initialConfig = {
                enabled: true,
                batch_size: 100,
                queue_name: "agent:siem_logs_queue",
                sources: []
            };
            saveConfig(initialConfig);
            return;
        }

        const updatedConfig = { ...configData.config, enabled: !configData.config.enabled };
        setConfigData({ ...configData, config: updatedConfig });
        saveConfig(updatedConfig);
    };

    const sources = configData?.config?.sources || [];
    const filteredSources = sources.filter((src: any) =>
        src.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (src.label && src.label.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const isGlobalEnabled = configData?.config?.enabled ?? false;

    const formatSourceTitle = (id: string) => {
        return id.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    return (
        <div className="p-3 h-full flex flex-col relative text-slate-900 dark:text-gray-200">
            <div className="flex items-center justify-between mb-3 border-b border-slate-200 dark:border-gray-700 pb-2 shrink-0">
                <div className="flex items-center">
                    {onBack && (
                        <button
                            onClick={onBack}
                            className="p-1.5 bg-orange-600 hover:bg-orange-700 text-white border border-orange-700 rounded-md transition-colors mr-3 flex items-center gap-1.5 text-xs font-semibold cursor-pointer animate-in fade-in duration-200 shadow-sm"
                            title="Back to Log Monitor"
                        >
                            <ArrowLeft className="w-3.5 h-3.5" />
                        </button>
                    )}
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5 shadow-sm">
                        <FileText className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">Log Monitor Settings</h2>
                        <p className="text-slate-500 dark:text-gray-200 text-[10px] uppercase font-semibold tracking-wider">
                            {sources.length} sources configured
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 mr-2">
                        <span className="text-sm text-slate-900 dark:text-white font-bold tracking-wider">
                            Global SIEM Logs
                        </span>
                        <button
                            onClick={handleToggleGlobal}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer ${isGlobalEnabled ? 'bg-green-500' : 'bg-slate-300 dark:bg-gray-600'
                                }`}
                        >
                            <span
                                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${isGlobalEnabled ? 'translate-x-4.5' : 'translate-x-1'
                                    }`}
                            />
                        </button>
                    </div>

                    <div className="relative w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-gray-500 w-3.5 h-3.5" />
                        <input
                            type="text"
                            placeholder="Search sources..."
                            className="w-full bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg pl-9 pr-4 py-1.5 text-[11px] text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-all font-medium shadow-sm"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <button
                        onClick={fetchConfig}
                        className="p-1.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white hover:border-blue-500/50 transition-all group shadow-sm cursor-pointer"
                        title="Refresh"
                    >
                        <RotateCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
                    </button>

                    <button
                        onClick={openAddModal}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold rounded-lg transition-colors shadow-sm cursor-pointer"
                    >
                        <Plus className="w-3.5 h-3.5" /> Add Source
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto custom-scroll">
                {loading ? (
                    <div className="flex justify-center items-center h-full">
                        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                    </div>
                ) : filteredSources.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-slate-500 dark:text-gray-500">
                        <Settings className="w-12 h-12 mb-3 text-slate-400 dark:text-gray-600" />
                        <h3 className="text-slate-900 dark:text-white font-semibold mb-1">No Sources Found</h3>
                        <p className="text-sm">No log sources match your criteria or none are configured.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {filteredSources.map((source: any) => {
                            const isDefault = DEFAULT_SOURCES.includes(source.id);

                            return (
                                <div key={source.id} className="bg-white dark:bg-gray-800/80 border border-slate-200 dark:border-gray-700/50 rounded-xl p-4 flex flex-col justify-between hover:border-blue-500/30 transition-all group shadow-sm">
                                    <div>
                                        <div className="flex justify-between items-start mb-2">
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-sm font-bold text-slate-900 dark:text-white break-all pr-2">
                                                    {source.label || formatSourceTitle(source.id)}
                                                </h3>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center gap-1 mr-1">
                                                    {(!isDefault || (source.params && Object.keys(source.params).length > 0)) && (
                                                        <button
                                                            onClick={() => openEditModal(source)}
                                                            className="text-slate-400 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 p-1 rounded transition-colors cursor-pointer"
                                                            title="Edit Source Settings"
                                                        >
                                                            <Edit className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                    {!isDefault && (
                                                        <button
                                                            onClick={() => handleDeleteSource(source.id)}
                                                            className="text-slate-400 dark:text-gray-500 hover:text-rose-600 dark:hover:text-red-400 p-1 rounded transition-colors cursor-pointer"
                                                            title="Delete Source"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                </div>
                                                <button
                                                    onClick={() => handleToggleSource(source.id)}
                                                    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors cursor-pointer ${source.enabled ? 'bg-green-500' : 'bg-slate-300 dark:bg-gray-600'
                                                        }`}
                                                    title={source.enabled ? "Disable Source" : "Enable Source"}
                                                >
                                                    <span
                                                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${source.enabled ? 'translate-x-4.5' : 'translate-x-1'
                                                            }`}
                                                    />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            {source.type && (
                                                <div className="text-[10px] text-slate-500 dark:text-gray-400">
                                                    <span className="font-semibold text-slate-400 dark:text-gray-500 uppercase tracking-widest mr-1">Type:</span>
                                                    <span className="px-1.5 py-0.5 bg-slate-100 dark:bg-gray-700 rounded text-slate-700 dark:text-gray-300 font-mono">{source.type}</span>
                                                </div>
                                            )}
                                            {source.params && Object.keys(source.params).length > 0 && (
                                                <div className="mt-2 text-[10px] text-slate-600 dark:text-gray-400 bg-slate-50 dark:bg-gray-900/50 p-2 rounded border border-slate-200 dark:border-gray-700/30 font-mono">
                                                    {source.params.paths && source.params.paths.length > 0 && (
                                                        <div className="mb-0.5"><span className="text-slate-400 dark:text-gray-500">paths:</span> <span className="text-slate-700 dark:text-gray-300 break-all">{source.params.paths.join(', ')}</span></div>
                                                    )}
                                                    {source.params.max_lines !== undefined && (
                                                        <div className="mb-0.5"><span className="text-slate-400 dark:text-gray-500">max_lines:</span> <span className="text-slate-700 dark:text-gray-300">{source.params.max_lines}</span></div>
                                                    )}
                                                    {source.params.poll_interval_seconds !== undefined && (
                                                        <div className="mb-0.5"><span className="text-slate-400 dark:text-gray-500">poll_interval:</span> <span className="text-slate-700 dark:text-gray-300">{source.params.poll_interval_seconds}s</span></div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {isModalOpen && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/60 dark:bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800/80">
                            <h3 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                {modalMode === "add" ? <Plus className="w-4 h-4 text-orange-500" /> : <Edit className="w-4 h-4 text-orange-500" />}
                                {modalMode === "add" ? "Add Log Source Configuration" : "Edit Log Source"}
                            </h3>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors p-1 cursor-pointer"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <form onSubmit={handleSaveSource} className="p-4 space-y-4 max-h-[70vh] overflow-y-auto custom-scroll">
                            <div className="relative">
                                <label className="block text-[11px] font-bold text-slate-600 dark:text-gray-400 uppercase tracking-widest mb-1.5">Label *</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                                    placeholder="Search or enter label"
                                    value={sourceLabel}
                                    onChange={(e) => {
                                        setSourceLabel(e.target.value);
                                        setShowServicesDropdown(true);
                                    }}
                                    onFocus={() => setShowServicesDropdown(true)}
                                    onBlur={() => setTimeout(() => setShowServicesDropdown(false), 200)}
                                    disabled={modalMode === "edit"}
                                />
                                {showServicesDropdown && modalMode === "add" && servicesList.length > 0 && (
                                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg shadow-xl max-h-48 overflow-y-auto custom-scroll">
                                        {servicesList
                                            .filter((s: any) => s.display_name && s.display_name.toLowerCase().includes(sourceLabel.toLowerCase()))
                                            .map((s: any) => (
                                                <div
                                                    key={s.id}
                                                    className="px-3 py-2 hover:bg-slate-100 dark:hover:bg-gray-800 cursor-pointer text-sm text-slate-900 dark:text-white transition-colors border-b border-slate-100 dark:border-gray-800 last:border-0"
                                                    onClick={() => {
                                                        setSourceLabel(s.display_name);
                                                        setShowServicesDropdown(false);
                                                    }}
                                                >
                                                    {s.display_name}
                                                </div>
                                            ))}
                                    </div>
                                )}
                            </div>
                            <div className="border-t border-slate-200 dark:border-gray-700 pt-3">
                                <h4 className="text-xs font-semibold text-slate-800 dark:text-gray-300 mb-3">Parameters</h4>

                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-[11px] font-bold text-slate-600 dark:text-gray-400 uppercase tracking-widest mb-1.5">Paths (Comma separated)</label>
                                        <input
                                            type="text"
                                            className="w-full bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors font-mono text-[11px]"
                                            placeholder="/var/log/syslog, /var/log/auth.log, /var/log/*.log"
                                            value={sourcePaths}
                                            onChange={(e) => setSourcePaths(e.target.value)}
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-[11px] font-bold text-slate-600 dark:text-gray-400 uppercase tracking-widest mb-1.5">Max Lines</label>
                                            <input
                                                type="number"
                                                className="w-full bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                                                value={sourceMaxLines}
                                                onChange={(e) => setSourceMaxLines(parseInt(e.target.value))}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-bold text-slate-600 dark:text-gray-400 uppercase tracking-widest mb-1.5">Poll Interval (Secs)</label>
                                            <input
                                                type="number"
                                                className="w-full bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                                                value={sourcePollInterval}
                                                onChange={(e) => setSourcePollInterval(parseInt(e.target.value))}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-[11px] font-bold text-slate-600 dark:text-gray-400 uppercase tracking-widest mb-1.5">Filter Keywords (Comma separated)</label>
                                        <input
                                            type="text"
                                            className="w-full bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                                            placeholder="error, warning, critical"
                                            value={sourceFilterKeywords}
                                            onChange={(e) => setSourceFilterKeywords(e.target.value)}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-[11px] font-bold text-slate-600 dark:text-gray-400 uppercase tracking-widest mb-1.5">Exclude Keywords (Comma separated)</label>
                                        <input
                                            type="text"
                                            className="w-full bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                                            placeholder="debug, trace, info"
                                            value={sourceExcludeKeywords}
                                            onChange={(e) => setSourceExcludeKeywords(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="pt-3 flex justify-end gap-2 border-t border-slate-200 dark:border-gray-700 mt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-4 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-slate-700 dark:text-white text-xs font-bold rounded-lg transition-colors cursor-pointer"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer shadow-sm"
                                >
                                    {modalMode === "add" ? "Add Source" : "Save Changes"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LogMonitorSettings;
